import React from "react";
import { ArrowLeft } from "lucide-react";
import { LogoFilterDefs } from "../components/landing/Logo";

/**
 * Vexly One Delete Account — public account-deletion instructions page
 * required by Apple App Store and Google Play.
 * Pure text content, bilingual (ES then EN).
 * Route: /one/eliminar-cuenta
 */
export default function VexlyOneDeleteAccount() {
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white antialiased">
      <LogoFilterDefs />

      <header className="border-b border-white/5">
        <div className="max-w-[760px] mx-auto px-5 md:px-8 h-16 flex items-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={15} />
            vexlyapp.com
          </a>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-16 text-[15px] md:text-base leading-[1.75] text-white/80">
        {/* ===== Español ===== */}
        <article lang="es">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10">
            Eliminar tu cuenta de Vexly One
          </h1>

          <Section title="Opción 1 — Desde la app">
            Ajustes → Eliminar mi cuenta. Te pedirá tu contraseña y escribir
            ELIMINAR.
          </Section>

          <Section title="Opción 2 — Por correo">
            Escribe a{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
              data-testid="delete-email-link-es"
            >
              soporte@vexlyapp.com
            </a>{" "}
            desde el correo registrado, con el asunto "Eliminar cuenta Vexly
            One", el nombre de tu empresa y tu usuario.
          </Section>

          <Section title="Qué se elimina">
            Tu usuario y tus datos personales. Si eres el dueño, se cierra la
            empresa: se desactivan todos los usuarios y se borran o anonimizan
            los datos personales de usuarios y clientes.
          </Section>

          <Section title="Qué se conserva">
            Comprobantes fiscales electrónicos y registros de ventas, por 10
            años, como exige la ley tributaria dominicana (Código Tributario,
            art. 50, literal h).
          </Section>

          <p className="text-sm text-white/50">
            Vexly One — BRIGHTWAY SOLUTIONS LLC
          </p>
        </article>

        <hr className="my-16 border-white/10" />

        {/* ===== English ===== */}
        <article lang="en">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10">
            Delete your Vexly One account
          </h1>

          <Section title="Option 1 — In the app">
            Settings → Delete my account. You will be asked for your password
            and to type ELIMINAR.
          </Section>

          <Section title="Option 2 — By email">
            Write to{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
              data-testid="delete-email-link-en"
            >
              soporte@vexlyapp.com
            </a>{" "}
            from your registered email, with the subject "Delete Vexly One
            account", your company name and username.
          </Section>

          <Section title="What is deleted">
            Your user and your personal data. If you are the owner, the
            company is closed: all users are deactivated and personal data of
            users and customers is deleted or anonymized.
          </Section>

          <Section title="What is kept">
            Electronic tax receipts and sales records, for 10 years, as
            required by Dominican tax law (Tax Code, art. 50(h)).
          </Section>

          <p className="text-sm text-white/50">
            Vexly One — BRIGHTWAY SOLUTIONS LLC
          </p>
        </article>

        <footer className="mt-16 pt-6 border-t border-white/5 text-xs text-white/40">
          <p>
            © 2026 BRIGHTWAY SOLUTIONS LLC. All rights reserved. ·{" "}
            <a href="/" className="hover:text-white underline-grow">
              vexlyapp.com
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-heading text-xl md:text-2xl font-semibold text-white mb-3 tracking-tight">
        {title}
      </h2>
      <div className="text-white/75">{children}</div>
    </section>
  );
}
